import { useEffect, useState } from "react"
import { getUsers, toggle_active_user } from "../../services/AuthServices"
import { useAuth } from "../../contexts/authContext"
import { UsersIcon, UserCheck, UserX, Search, Mail, Calendar, Shield } from "lucide-react"
import "../../assets/styles/Users.css" 
import { formatDate } from "../../utils/dates/formatDate"

const Users = () => {
  const { getToken } = useAuth()
  const [users, setUsers] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState("")
  const [togglingUserId, setTogglingUserId] = useState(null)

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const token = await getToken()
        const data = await getUsers(token)
        setUsers(data)
      } catch (error) {
        console.error("Error al obtener los usuarios:", error)
      } finally {
        setLoading(false)
      }
    }

    fetchUsers()
  }, [getToken])

  const handleToggleActive = async (userId, currentStatus) => {
    try {
      setTogglingUserId(userId) 

      const token = await getToken()
      const success = await toggle_active_user(token, userId)

      if (success) {
        setUsers(users.map((user) => (user.uid === userId ? { ...user, active: !currentStatus } : user)))
      } else {
        console.error("Error al cambiar el estado del usuario")
      }
    } catch (error) {
      console.error("Error al cambiar el estado del usuario:", error)
    } finally {
      setTogglingUserId(null) 
    }
  }

  const filteredUsers = users.filter(
    (user) =>
      `${user.firstName} ${user.lastName}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase()),
  )

  return (
    <div className="users-container">
      <div className="users-header">
        <h1 className="users-title">
          <UsersIcon className="users-icon" />
          Gestión de Usuarios
        </h1>
        <div className="search-container">
          <Search className="search-icon" />
          <input
            type="text"
            placeholder="Buscar usuarios..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
      </div>

      <div className="users-stats">
        <div className="stat-card">
          <UserCheck className="stat-icon active" />
          <div className="stat-info">
            <span className="stat-value">{users.filter((user) => user.active).length}</span>
            <span className="stat-label">Usuarios Activos</span>
          </div>
        </div>
        <div className="stat-card">
          <UserX className="stat-icon inactive" />
          <div className="stat-info">
            <span className="stat-value">{users.filter((user) => !user.active).length}</span>
            <span className="stat-label">Usuarios Inactivos</span>
          </div>
        </div>
        <div className="stat-card">
          <UsersIcon className="stat-icon total" />
          <div className="stat-info">
            <span className="stat-value">{users.length}</span>
            <span className="stat-label">Total Usuarios</span>
          </div>
        </div>
      </div>

      <div className="users-table-container">
        {loading ? (
          <table className="users-table">
            <thead>
              <tr>
                <th className="user-col-avatar"></th>
                <th className="user-col-name">Usuario</th>
                <th className="user-col-role">Rol</th>
                <th className="user-col-date">Fecha de registro</th>
                <th className="user-col-actions">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {[...Array(5)].map((_, index) => (
                <tr key={index} className="user-row skeleton-row">
                  <td className="user-col-avatar">
                    <div className="skeleton user-avatar-skeleton"></div>
                  </td>
                  <td className="user-col-name">
                    <div className="skeleton skeleton-text-lg"></div>
                    <div className="skeleton skeleton-text-sm"></div>
                  </td>
                  <td className="user-col-role">
                    <div className="skeleton skeleton-text-sm"></div>
                  </td>
                  <td className="user-col-date">
                    <div className="skeleton skeleton-text-sm"></div>
                  </td>
                  <td className="user-col-actions">
                    <div className="skeleton skeleton-button"></div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <table className="users-table">
            <thead>
              <tr>
                <th className="user-col-avatar"></th>
                <th className="user-col-name">Usuario</th>
                <th className="user-col-role">Rol</th>
                <th className="user-col-date">Fecha de registro</th>
                <th className="user-col-actions">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.length === 0 ? (
                <tr>
                  <td colSpan="5">
                    <div className="no-results">
                      <p>No se encontraron usuarios que coincidan con la búsqueda.</p>
                    </div>
                  </td>
                </tr>
              ) : (
                filteredUsers.map((user) => (
                  <tr key={user.uid} className="user-row">
                    <td className="user-col-avatar">
                      <div className="user-avatar-container">
                        <img
                          src={user.profilePicture || "/placeholder.svg?height=100&width=100"}
                          alt={`${user.firstName} ${user.lastName}`}
                          className="user-avatar"
                          onError={(e) => {
                            e.target.src = "/placeholder.svg?height=100&width=100"
                          }}
                        />
                        <span className={`status-indicator ${user.active ? "active" : "inactive"}`}></span>
                      </div>
                    </td>
                    <td className="user-col-name">
                      <div className="user-name2">
                        {user.firstName} {user.lastName}
                      </div>
                      <div className="user-email">
                        <Mail className="info-icon" />
                        {user.email}
                      </div>
                    </td>
                    <td className="user-col-role">
                      <div className="user-role">
                        <Shield className="info-icon" />
                        {user.role.name}
                      </div>
                    </td>
                    <td className="user-col-date">
                      <div className="user-date">
                        <Calendar className="info-icon" />
                        {formatDate(user.registrationDate)}
                      </div>
                    </td>
                    <td className="user-col-actions">
                      <button
                        className={`btn ${user.active ? "btn-destructive" : "btn-primary"}`}
                        onClick={() => handleToggleActive(user.uid, user.active)}
                        disabled={togglingUserId === user.uid} 
                      >
                        {togglingUserId === user.uid ? (
                          <span className="loading-spinner"></span>
                        ) : user.active ? (
                          <>
                            <UserX className="btn-icon" />
                            <span className="btn-text">Desactivar</span>
                          </>
                        ) : (
                          <>
                            <UserCheck className="btn-icon" />
                            <span className="btn-text">Activar</span>
                          </>
                        )}
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}

export default Users
